import { EntityId } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';

import { getCurrentLocationId } from 'selectors/session';

import { getSliceState } from 'features/scheduleBuilder/slices/helpers';
import {
  selectAllJobs,
  selectJobAttribute,
  selectJobRelationshipId,
} from 'features/scheduleBuilder/slices/jobs/slice';
import { selectLocationAttribute } from 'features/scheduleBuilder/slices/locations/slice';
import {
  selectAllRoles,
  selectRoleById,
} from 'features/scheduleBuilder/slices/roles/slice';
import {
  RoleOption,
  RoleShiftProps,
} from 'features/scheduleBuilder/slices/roles/types';
import { selectColorMappingVersion } from 'features/scheduleBuilder/slices/session/slice';
import { SLICE_NAME as SHIFTS_SLICE_NAME } from 'features/scheduleBuilder/slices/shifts/constants';
import { shiftSortCompare } from 'features/scheduleBuilder/slices/shifts/helpers';
import { ShiftRelationshipKeys } from 'features/scheduleBuilder/slices/shifts/types';
import { isOvernight } from 'features/scheduleBuilder/util';

import { dateFromTime } from 'util/dateTime';

import { SLICE_NAME } from '../constants';
import { getDefaultShiftColor } from '../helpers';

// Basic shift selectors that don't depend on the main slice to break circular dependencies
export const selectShiftsState = (state: any) =>
  state.get('scheduleBuilderSchedule')?.shifts;

export const selectAllNonPendingShifts = createSelector(
  selectShiftsState,
  shiftsState =>
    shiftsState?.entities
      ? Object.values(shiftsState.entities).filter(Boolean)
      : []
);

const selectSlice = (state: any) => state.get(SLICE_NAME) || {};

const selectScheduleBuilderViewState = (state: RootState) =>
  state.get(SLICE_NAME) || {};

export const selectCurrentDateRange = createSelector(
  selectScheduleBuilderViewState,
  slice => slice.currentDateRange
);

export const selectCurrentDateRangeStart = createSelector(
  selectCurrentDateRange,
  currentDateRange => currentDateRange?.start
);

export const selectCurrentDateRangeEnd = createSelector(
  selectCurrentDateRange,
  currentDateRange => currentDateRange?.end
);

const getCurrentLocationIdForSchedule = (state: RootState) =>
  String(
    getCurrentLocationId(state) || state.getIn(['session'])?.currentLocation?.id
  );

export const selectJobsForCurrentLocation = createSelector(
  getCurrentLocationIdForSchedule,
  selectAllJobs,
  (currentLocationId, jobs) =>
    jobs?.filter(job => job.id && job.location?.id === currentLocationId) || []
);

export const selectJobIdUserIdMap = createSelector(
  selectJobsForCurrentLocation,
  jobs =>
    jobs?.reduce((memo: Record<string, string>, job: any) => {
      if (job?.user?.id) {
        memo[job.id] = job.user.id;
      }
      return memo;
    }, {})
);

export const selectDefaultRoleOption = createCachedSelector(
  (
    state: RootState,
    { jobId, isAdding }: { jobId: EntityId; isAdding: boolean }
  ) => {
    const defaultRoleId = selectJobAttribute({
      state,
      attribute: 'defaultRoleId',
      id: jobId,
    });
    const wageRate = selectJobAttribute({
      state,
      attribute: 'wageRate',
      id: jobId,
    });

    const defaultRole: {
      label: string;
      value: string | null;
      color: string;
      wage: number | undefined;
    } = {
      label: 'No Role',
      value: null,
      color: getDefaultShiftColor(selectColorMappingVersion(state) === 1),
      wage: wageRate,
    };

    /** When adding a new shift, use the users default role, else return 'No Role' */
    if (isAdding && defaultRoleId) {
      const defaultRoleEntity = selectRoleById(state, defaultRoleId.toString());

      if (defaultRoleEntity) {
        defaultRole.value = defaultRoleEntity.name;
        defaultRole.label = defaultRoleEntity.name;
        defaultRole.color = defaultRoleEntity.color;
      }
    }

    return defaultRole;
  },
  defaultRole => defaultRole
)((_, { jobId, isAdding }) => `selectDefaultRoleOption-${jobId}-${isAdding}`);

// Merge entities with pending taking priority
const mergeEntities = (entities: any[]) =>
  entities.reduce(
    (mergedEntity, pendingEntity) => ({
      ...mergedEntity,
      attributes: {
        ...(mergedEntity.attributes || {}),
        ...(pendingEntity.attributes || {}),
      },
      relationships: {
        ...(mergedEntity.relationships || {}),
        ...(pendingEntity.relationships || {}),
      },
      // Direct properties (for backward compatibility)
      ...pendingEntity,
    }),
    {}
  );

/* Use this selectAll selector in order to receive all shifts */
/* including optimistic artificial ones */
export const selectAllShifts = createSelector(
  [
    (state: RootState) => selectAllNonPendingShifts(state),
    (state: RootState) => {
      const sliceState = getSliceState(state)?.[SHIFTS_SLICE_NAME] || {
        pending: { stack: [], index: {} },
      };
      return (
        sliceState.pending || {
          stack: [],
          index: {},
        }
      );
    },
  ],
  (savedShifts: any[], pendingState: any) => {
    const shiftsMap = new Map();
    const pendingShiftIds = new Set();

    // First, collect all original shift IDs that have pending versions
    pendingState.stack.forEach((pendingId: string) => {
      const pendingData = pendingState.index[pendingId];
      if (pendingData?.shift) {
        Object.entries(pendingData.shift).forEach(() => {
          // Extract original shift ID from pending ID format:
          // pending_{shiftId}_{timestamp}_{random}
          if (pendingId.startsWith('pending_')) {
            const parts = pendingId.split('_');
            if (parts.length >= 2) {
              const originalShiftId = parts[1];
              pendingShiftIds.add(originalShiftId);
            }
          }
        });
      }
    });

    // Add saved shifts
    // Add saved shifts only if they don't have pending versions
    savedShifts.forEach((shift: any) => {
      if (!pendingShiftIds.has(shift.id)) {
        shiftsMap.set(shift.id, shift);
      }
    });

    // Add pending shifts (these replace any saved versions)
    pendingState.stack.forEach((pendingId: string) => {
      const pendingData = pendingState.index[pendingId];
      if (pendingData?.shift) {
        Object.entries(pendingData.shift).forEach(
          ([id, pendingShift]: [string, any]) => {
            // Find the original saved shift to merge with
            let originalShiftId: undefined | string;
            if (pendingId.startsWith('pending_')) {
              const parts = pendingId.split('_');
              if (parts.length >= 2) {
                originalShiftId = parts[1];
              }
            }

            const savedShift = originalShiftId
              ? savedShifts.find((s: any) => s.id === originalShiftId)
              : null;
            if (savedShift) {
              const merged = mergeEntities([savedShift, pendingShift]);
              shiftsMap.set(originalShiftId, { ...merged, _isPending: true });
            } else {
              shiftsMap.set(id, { ...pendingShift, _isPending: true });
            }
          }
        );
      }
    });

    return Array.from(shiftsMap.values());
  }
);

const selectShiftById = createSelector(
  selectAllShifts,
  (_: RootState, id: EntityId) => id,
  (shifts: any[], id: EntityId) => shifts.find((shift: any) => shift.id === id)
);

export const selectShiftsForDay = createCachedSelector(
  selectAllShifts,
  (_: RootState, { date }: { date: string }) => date,
  (shifts: any[], date: string) =>
    shifts
      .filter((s: any) => dateFromTime(s.startAt) === date)
      .sort(shiftSortCompare)
)((_, props) => `dayView-selectShiftsForDay-${props.date}`);

export const selectOvernightShiftsForDay = createCachedSelector(
  selectShiftsForDay,
  shifts =>
    shifts.filter(s => isOvernight(s.startAt, s.endAt)).sort(shiftSortCompare)
)((_, props) => `dayView-selectOvernightShiftsForDay-${props.date}`);

export const selectJobIdWageRateMap = createSelector(selectAllJobs, jobs =>
  jobs?.reduce((memo: Record<string, number>, job: any) => {
    if (job?.wageRate) {
      memo[job.id] = job.wageRate;
    }
    return memo;
  }, {})
);

export const selectShiftRelationshipEntity = ({
  state,
  id,
  relationship,
}: {
  state: RootState;
  id: EntityId;
  relationship: ShiftRelationshipKeys;
}):
  | { id: string; type: 'owner' | 'role' | 'shiftNote' | 'conflict' }
  | undefined => {
  const shift = selectShiftById(state, id);

  if (!shift) return undefined;

  if (relationship === 'owner') {
    return shift.owner;
  }
  if (relationship === 'role') {
    return shift.role;
  }
  if (relationship === 'note') {
    return shift.note;
  }
  if (relationship === 'conflict') {
    return shift.conflict;
  }

  return undefined;
};

export const selectJobIdsForCurrentLocation = createSelector(
  state =>
    selectJobsForCurrentLocation(state)
      .map(job => job.id)
      .join(','),
  ids => (ids.length ? ids.split(',') : [])
);

export const selectOpenTime = (state: any) =>
  selectLocationAttribute({
    state,
    attribute: 'openTime',
    id: getCurrentLocationIdForSchedule(state),
  });

export const selectClosingTime = (state: any) =>
  selectLocationAttribute({
    state,
    attribute: 'closingTime',
    id: getCurrentLocationIdForSchedule(state),
  });

export const selectConflictsEnabled = createSelector(
  selectSlice,
  slice => slice.conflictsEnabled
);

export const selectIsJumpstartClosed = createSelector(
  selectSlice,
  slice => slice.isJumpstartClosed
);

export const selectIsShiftsAtThisLocationOnly = createSelector(
  selectSlice,
  slice => slice.isShiftsAtThisLocationOnly
);

/* cloned selectors from slices */

export const selectShiftRoleOption = createCachedSelector(
  (
    state: RootState,
    { roleId: id, shiftId }: RoleShiftProps
  ): RoleOption | null => {
    const relationshipEntity = selectShiftRelationshipEntity({
      state,
      id: shiftId,
      relationship: 'role',
    });

    const roleId = relationshipEntity?.id || id;
    if (!roleId) return null;

    const role = selectRoleById(state, roleId);
    if (!role) return null;

    return {
      value: role.name,
      label: role.name,
      color: role.color,
    };
  },
  (option: any) => option
)(
  (_: any, { shiftId, roleId }: RoleShiftProps) =>
    `selectShiftRoleOption-${shiftId}-${roleId}`
);

export const selectShiftsByRoleId = createCachedSelector(
  (state: RootState, roleId: EntityId) => ({
    roleId,
    entities: selectAllShifts(state),
  }),
  ({ entities, roleId }) =>
    entities.filter((entity: any) => {
      const entityRoleId = entity.role?.id;
      return entityRoleId === roleId;
    })
)((_, __, roleId) => `selectShiftsByRoleId-${roleId}`);

// eslint-disable-next-line
//@ts-ignore
export const selectRoleByName = createSelector(
  [
    (state: RootState) => selectAllRoles(state),
    (_: RootState, name: string) => name,
  ],
  (roles, name) => roles.find((role: any) => role.name === name)
);

export const selectUserJobs = createCachedSelector(
  [
    (state: RootState) => selectAllJobs(state),
    (_: unknown, userId: EntityId) => userId,
  ],
  (allJobs, userId) => {
    const userJobs = allJobs.filter((job: any) => job.user?.id === userId);
    return userJobs;
  }
)((_, userId: EntityId) => `selectUserJobs-${userId}`);

export const selectJobIdsForUser = createCachedSelector(
  (state: RootState, props: { jobId: EntityId }) => {
    const userId = selectJobRelationshipId({
      state,
      relationship: 'user',
      id: props.jobId,
    });

    const jobs =
      userId && !Array.isArray(userId) ? selectUserJobs(state, userId) : [];
    return jobs.map((job: any) => job.id).join(',');
  },
  jobs => (jobs.length ? jobs.split(',') : [])
)((_, props: { jobId: EntityId }) => `selectJobIdsForUser-${props.jobId}`);

export const decorateShift = (shift: any) => ({
  ...shift,
  decoration: {
    date: dateFromTime(shift.startAt),
  },
});

export const selectDecoratedShifts = createSelector(selectAllShifts, shifts =>
  shifts.map(decorateShift)
);

export const selectShiftIdJobIdMap = createSelector(
  selectDecoratedShifts,
  shifts =>
    shifts?.reduce((memo: any, shift: any) => {
      if (shift?.owner?.id) {
        memo[shift.id] = shift?.owner?.id;
      }
      return memo;
    }, {})
);
